/** @jsx jsx */
import React, { type PropsWithChildren } from 'react';
import { jsx } from '@compiled/react';

import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import CommentCard from '@atlassian/jira-comment/src/ui/card.tsx';

import Comment from '@atlassian/jira-comment/src/ui/index.tsx';
import CommentHeader from '@atlassian/jira-comment/src/ui/header/index.tsx';
import CommentAvatar from '@atlassian/jira-comment/src/ui/header/avatar.tsx';
import CommentAuthor from '@atlassian/jira-comment/src/ui/header/author.tsx';
import CommentBody from '@atlassian/jira-comment/src/ui/body.tsx';
import CommentFooter from '@atlassian/jira-comment/src/ui/footer.tsx';
import CommentHeaderSubtext from '@atlassian/jira-comment/src/ui/header/subtext.tsx';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../messages.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';
import { useAuthor, useAuthorAdditionalInfo } from './utils.tsx';
import CommentContent from './CommentContent.tsx';
import CommentTime from './CommentTime.tsx';
import CommentAuthorActivity from './CommentAuthorActivity.tsx';
import CommentLozenge from './CommentLozenge.tsx';
import CommentStatus from './CommentStatus.tsx';
import CommentEditedIndicator from './CommentEditedIndicator.tsx';
import CommentActions from './CommentActions.tsx';
import CommentSavingIndicator from './CommentSavingIndicator.tsx';
import SlackLink from './SlackLink.tsx';

const CommentDefault = () => {
	const {
		isThreadedCommentsEnabled,
		commentId,
		parentId,
		hasReplies,
		isReplyingToComment,
		showLozenge,
		isSaving,
		isDeleted,
		isError,
	} = useCommentContext();
	const authorAdditionalInfo = useAuthorAdditionalInfo();
	const author = useAuthor();
	const isParent = isThreadedCommentsEnabled && (hasReplies || isReplyingToComment);
	const isNested = isThreadedCommentsEnabled && !!parentId;

	return (
		<Comment
			testId={`issue-comment-base.ui.comment.ak-comment.${commentId}`}
			showThreadLines={isParent}
		>
			<CommentHeader testId={`issue-comment-base.ui.comment.ak-comment.${commentId}-header`}>
				<CommentAvatar user={author} isNested={isNested} />
				<CommentAuthor
					user={author}
					authorInfo={authorAdditionalInfo}
					authorActivity={<CommentAuthorActivity />}
				/>
				<CommentStatus />
				<CommentHeaderSubtext space="space.050">
					{!isDeleted && !isSaving && !isError && <CommentTime />}
					{!isDeleted && !isSaving && <SlackLink />}
					{!isDeleted && !isSaving && <CommentEditedIndicator />}
					{isSaving && <CommentSavingIndicator />}
				</CommentHeaderSubtext>
			</CommentHeader>

			<CommentBody testId={`issue-comment-base.ui.comment.ak-comment.${commentId}-body`}>
				{showLozenge && (
					<Stack alignInline="start">
						<CommentLozenge />
					</Stack>
				)}
				<CommentContent />
			</CommentBody>

			{!isSaving && (
				<CommentFooter testId={`issue-comment-base.ui.comment.ak-comment.${commentId}-footer`}>
					<CommentActions />
				</CommentFooter>
			)}
		</Comment>
	);
};

const deletedTemplateAreas = ['header header'];
const CommentDeleted = () => {
	const { isThreadedCommentsEnabled, commentId, hasReplies, isReplyingToComment } =
		useCommentContext();
	const { formatMessage } = useIntl();
	const isParent = isThreadedCommentsEnabled && (hasReplies || isReplyingToComment);

	return (
		<Comment
			testId={`issue-comment-base.ui.comment.ak-comment.${commentId}`}
			showThreadLines={isParent}
			templateAreas={deletedTemplateAreas}
		>
			<CommentHeader testId={`issue-comment-base.ui.comment.ak-comment.${commentId}-header`}>
				<CommentAvatar />
				<Flex alignItems="center">
					<Text color="color.text.subtle">{formatMessage(messages.commentDeleted)}</Text>
				</Flex>
			</CommentHeader>
		</Comment>
	);
};

const CommentComponent = ({ children }: PropsWithChildren) => {
	const {
		isDeleted,
		isThreadedCommentsEnabled,
		hasReplies,
		isReplyingToComment,
		isInternal,
		shouldHighlightComment,
	} = useCommentContext();
	const isParent = isThreadedCommentsEnabled && (hasReplies || isReplyingToComment);

	return (
		<Stack>
			<CommentCard
				appearance={isInternal ? 'internal' : 'legacy'}
				isFocused={shouldHighlightComment}
			>
				{isDeleted ? <CommentDeleted /> : <CommentDefault />}
			</CommentCard>
			{isParent && <Box xcss={nestedChildrenWrapperStyles}>{children}</Box>}
		</Stack>
	);
};

export default CommentComponent;

const nestedChildrenWrapperStyles = xcss({
	marginLeft: 'space.400',
	paddingLeft: 'space.150',
});
